import type { MedicationType } from '@/components/Patient/components/ProductDetailsCard/ProductDetailsCard.types';
import BigNumber from 'bignumber.js';

import { DISPENSE_PERIOD_DAYS } from './constants';
import { roundUpDispenseAmount } from './roundUpDispenseAmount';

/**
 * The fields of a pad item's medication the amount is calculated from, so the medication itself can be passed.
 * `dispenseSize` is the net weight of a single unit of the product, e.g. 10 for a 10g jar.
 */
type DispenseAmountInput = Pick<MedicationType, 'dispenseSize' | 'quantity' | 'interval'>;

// pms-service sends a net weight of 0 for a dried herb product whose size is missing or unreadable
const UNREADABLE_NET_WEIGHT = 0;

/** Whether a net weight can be dispensed from: present, and not the sentinel for one pms-service could not read */
const isReadableNetWeight = (dispenseSize: number | undefined): dispenseSize is number =>
  dispenseSize !== undefined && dispenseSize !== UNREADABLE_NET_WEIGHT;

/**
 * Whether a chosen product's net weight is missing, or pms-service could not read it. A product sent without one is
 * treated as unreadable too: either way its dispense amount cannot be worked out.
 *
 * Only once the product's details have loaded, which every product write marks by setting `dispenseSize`, even to
 * undefined; each write's spec pins it. A reissued item whose product is not in the doctor's product list never loads
 * them: it has no amount of its own, but does not turn dispense amounts off for its formulation.
 */
export const hasUnreadableNetWeight = (medication: DispenseAmountInput | undefined) =>
  medication !== undefined && 'dispenseSize' in medication && !isReadableNetWeight(medication.dispenseSize);

/**
 * How much of a product a script dispenses over 30 days, net weight × quantity × (30 days ÷ interval), rounded up at
 * the first decimal; undefined when the inputs can't produce one, an unreadable net weight included.
 */
export const calculateDispenseAmount = ({ dispenseSize, quantity, interval }: DispenseAmountInput = {}) => {
  // The values as stored, with no product defaults, so a row and its share of the formulation total always agree
  if (!isReadableNetWeight(dispenseSize) || !quantity || !interval) {
    return undefined;
  }

  // Dividing last keeps the one inexact step to the end, where BigNumber carries it at 20 decimal places
  return roundUpDispenseAmount(
    BigNumber(dispenseSize).times(quantity).times(DISPENSE_PERIOD_DAYS).dividedBy(interval),
  ).toNumber();
};

/** Dispense amounts added exactly, so a total of rounded amounts stays on one decimal. */
export const sumDispenseAmounts = (amounts: number[]) =>
  amounts.reduce((total, amount) => total.plus(amount), BigNumber(0)).toNumber();
